/**
 * Persian Language Service
 * Provides comprehensive Persian language support including RTL handling,
 * font management, text processing, and CSS property application.
 */

import { PersianMetadata } from '../types';
import { LEARNER_PERSIAN_FONT_OPTIONS } from '../constants/learnerPersianFonts';
import { 
  containsPersian, 
  isPrimaryPersian, 
  validatePersianText, 
  normalizePersianText,
  toPersianDigits,
  toEnglishDigits,
  getTextDirection
} from '../utils/persianUtils';

export interface PersianLanguageConfig {
  defaultFont: string;
  fallbackFonts: string[];
  enableAutoDirection: boolean;
  enablePersianDigits: boolean;
  enableFontPreloading: boolean;
  cssVariables: Record<string, string>;
}

export interface FontLoadResult {
  font: string;
  loaded: boolean;
  error?: string;
}

export interface TextAnalysis {
  direction: 'rtl' | 'ltr' | 'auto';
  containsPersian: boolean;
  isPrimaryPersian: boolean;
  wordCount: number;
  characterCount: number;
  persianCharacterCount: number;
  englishCharacterCount: number;
}

export interface PersianStyleOptions {
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  textAlign?: 'right' | 'left' | 'center' | 'justify' | 'auto';
  direction?: 'rtl' | 'ltr' | 'auto';
  unicodeBidi?: 'normal' | 'embed' | 'isolate' | 'bidi-override' | 'isolate-override' | 'plaintext';
}

class PersianLanguageService {
  private config: PersianLanguageConfig;
  private loadedFonts: Set<string> = new Set();
  private fontLoadPromises: Map<string, Promise<FontLoadResult>> = new Map();
  private cssVariablesApplied: boolean = false;

  constructor(config?: Partial<PersianLanguageConfig>) {
    this.config = {
      defaultFont: 'Vazirmatn',
      fallbackFonts: ['Tahoma', 'Arial', 'sans-serif'],
      enableAutoDirection: true,
      enablePersianDigits: false,
      enableFontPreloading: true,
      cssVariables: {
        '--font-persian': 'Vazirmatn, Tahoma, Arial, sans-serif',
        '--font-persian-size': '16px',
        '--font-persian-weight': '400',
        '--font-persian-line-height': '1.6',
        '--color-persian-text': '#2d3748',
        '--color-persian-accent': '#3182ce',
      },
      ...config,
    };

    // Defer warmup so circular production chunks can finish initializing first.
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => {
        void this.initializeService();
      });
    } else {
      void this.initializeService();
    }
  }

  private async initializeService(): Promise<void> {
    // Apply CSS variables
    this.applyCSSVariables();

    // Preload default Persian fonts
    if (this.config.enableFontPreloading) {
      await this.preloadPersianFonts();
    }

    // Set up font loading detection
    this.setupFontLoadingDetection();
  }

  private applyCSSVariables(): void {
    if (this.cssVariablesApplied) return;

    // Temporarily disabled to prevent PostCSS errors
    // const style = document.createElement('style');
    // style.id = 'persian-language-variables';
    // 
    // const cssRules = Object.entries(this.config.cssVariables)
    //   .map(([property, value]) => `  ${property}: ${value};`)
    //   .join('\n');
    //
    // style.textContent = `:root {\n${cssRules}\n}`;
    // document.head.appendChild(style);
    this.cssVariablesApplied = true;
  }

  private async preloadPersianFonts(): Promise<void> {
    const fontsToLoad = [
      this.config.defaultFont,
      ...this.config.fallbackFonts.filter(font => font !== 'Arial' && font !== 'sans-serif')
    ];

    const loadPromises = fontsToLoad.map(font => this.loadPersianFont(font));
    await Promise.allSettled(loadPromises);
  }

  private setupFontLoadingDetection(): void {
    if ('fonts' in document) {
      document.fonts.addEventListener('loadingdone', () => {
        // Refresh any Persian text elements when fonts finish loading
        this.refreshPersianElements();
      });
    }
  }

  private refreshPersianElements(): void {
    const persianElements = document.querySelectorAll('[data-persian="true"]');
    persianElements.forEach(element => {
      if (element instanceof HTMLElement) {
        const text = element.textContent || '';
        this.applyPersianStyles(element, text);
      }
    });
  }

  /**
   * Load a Persian font with proper error handling and caching
   */
  async loadPersianFont(fontName: string, weights: number[] = [300, 400, 500, 700]): Promise<FontLoadResult> {
    // Check if already loaded
    if (this.loadedFonts.has(fontName)) {
      return { font: fontName, loaded: true };
    }

    // Check if loading is in progress
    if (this.fontLoadPromises.has(fontName)) {
      return this.fontLoadPromises.get(fontName)!;
    }

    // Start loading
    const loadPromise = this.performFontLoad(fontName, weights);
    this.fontLoadPromises.set(fontName, loadPromise);

    return loadPromise;
  }

  private async performFontLoad(fontName: string, weights: number[]): Promise<FontLoadResult> {
    try {
      await this.loadBundledPersianFaces(fontName, weights);
      this.loadedFonts.add(fontName);
      return { font: fontName, loaded: true };
    } catch (err) {
      const error = `Failed to warm up font ${fontName}: ${err}`;
      console.warn(error);
      return { font: fontName, loaded: false, error };
    }
  }

  /** Warm up app-bundled Arabic/Persian faces (no external font CDNs). */
  private async loadBundledPersianFaces(_fontName: string, weights: number[]): Promise<void> {
    if (!('fonts' in document)) return;
    const bundled = LEARNER_PERSIAN_FONT_OPTIONS.filter((o): o is (typeof o & { cssFamily: string }) => !!o.cssFamily);
    await Promise.all(
      bundled.flatMap((o) =>
        weights.map((w) => document.fonts.load(`${w} 16px "${o.cssFamily}"`).catch(() => undefined))
      )
    );
  }

  /**
   * Detect RTL text and return appropriate direction
   */
  detectTextDirection(text: string): 'rtl' | 'ltr' | 'auto' {
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      return 'auto';
    }

    return getTextDirection(text);
  }

  /**
   * Apply Persian styles to an HTML element
   */
  applyPersianStyles(
    element: HTMLElement, 
    text?: string, 
    options: PersianStyleOptions = {}
  ): void {
    const textToAnalyze = text || element.textContent || '';
    const analysis = this.analyzeText(textToAnalyze);

    // Mark element as Persian for future reference
    element.setAttribute('data-persian', analysis.containsPersian.toString());

    // Apply font family
    const fontFamily = this.buildFontFamily();
    element.style.fontFamily = fontFamily;

    // Apply direction
    const direction = options.direction || (this.config.enableAutoDirection ? analysis.direction : 'auto');
    if (direction !== 'auto') {
      element.style.direction = direction;
    }

    // Apply unicode-bidi
    const unicodeBidi = options.unicodeBidi || (analysis.containsPersian ? 'plaintext' : 'normal');
    element.style.unicodeBidi = unicodeBidi;

    // Apply text alignment
    if (options.textAlign) {
      element.style.textAlign = options.textAlign;
    } else if (analysis.isPrimaryPersian) {
      element.style.textAlign = 'right';
    }

    // Apply other styling options
    if (options.fontSize) element.style.fontSize = options.fontSize;
    if (options.fontWeight) element.style.fontWeight = options.fontWeight;
    if (options.lineHeight) element.style.lineHeight = options.lineHeight;

    // Apply CSS variables
    element.style.setProperty('--text-direction', direction);
    element.style.setProperty('--is-persian', analysis.containsPersian.toString());
  }

  /**
   * Build font family string with fallbacks
   */
  private buildFontFamily(): string {
    const fonts = [this.config.defaultFont, ...this.config.fallbackFonts];
    return fonts.map(font => font.includes(' ') ? `"${font}"` : font).join(', ');
  }

  /**
   * Analyze text for Persian language characteristics
   */
  analyzeText(text: string): TextAnalysis {
    // Ensure text is a string
    if (!text || typeof text !== 'string') {
      text = String(text || '');
    }
    
    const direction = this.detectTextDirection(text);
    const hasPersian = containsPersian(text);
    const primaryPersian = isPrimaryPersian(text);
    
    // Count characters
    const totalChars = text.length;
    const persianChars = (text.match(/[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/g) || []).length;
    const englishChars = (text.match(/[A-Za-z]/g) || []).length;
    
    // Count words (approximate)
    const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;

    return {
      direction,
      containsPersian: hasPersian,
      isPrimaryPersian: primaryPersian,
      wordCount: words,
      characterCount: totalChars,
      persianCharacterCount: persianChars,
      englishCharacterCount: englishChars,
    };
  }

  /**
   * Process Persian metadata from API responses
   */
  processPersianMetadata(metadata: PersianMetadata): void {
    // Load recommended fonts
    if (metadata.recommendedFonts?.length > 0) {
      metadata.recommendedFonts.forEach(font => {
        this.loadPersianFont(font).catch(error => {
          console.warn(`Failed to load recommended font ${font}:`, error);
        });
      });
    }

    // Apply CSS properties
    if (metadata.cssProperties) {
      this.applyCSSProperties(metadata.cssProperties);
    }

    // Update global settings if needed
    if (metadata.textDirection) {
      document.documentElement.setAttribute('data-text-direction', metadata.textDirection);
    }
  }

  /**
   * Apply CSS properties from metadata
   */
  private applyCSSProperties(cssProperties: Record<string, string>): void {
    // Temporarily disabled to prevent PostCSS errors
    // const style = document.createElement('style');
    // style.className = 'persian-metadata-styles';
    // 
    // const cssRules = Object.entries(cssProperties)
    //   .map(([selector, properties]) => `${selector} { ${properties} }`)
    //   .join('\n');
    //
    // style.textContent = cssRules;
    // document.head.appendChild(style);
  }

  /**
   * Format Persian text for display with proper styling
   */
  formatPersianText(
    text: string, 
    options: {
      convertDigits?: boolean;
      normalize?: boolean;
      direction?: 'rtl' | 'ltr' | 'auto';
      applyFormatting?: boolean;
    } = {}
  ): {
    text: string;
    direction: 'rtl' | 'ltr' | 'auto';
    className: string;
    styles: Record<string, string>;
  } {
    let processedText = text;

    // Normalize text if requested
    if (options.normalize) {
      processedText = normalizePersianText(processedText);
    }

    // Convert digits if requested
    if (options.convertDigits && this.config.enablePersianDigits) {
      processedText = toPersianDigits(processedText);
    }

    // Determine direction
    const direction = options.direction || this.detectTextDirection(processedText);
    
    // Generate appropriate class name
    const className = this.generateClassName(processedText, direction);
    
    // Generate inline styles if needed
    const styles: Record<string, string> = {};
    if (options.applyFormatting) {
      styles.fontFamily = this.buildFontFamily();
      styles.direction = direction;
      styles.unicodeBidi = 'plaintext';
      
      if (direction === 'rtl') {
        styles.textAlign = 'right';
      }
    }

    return {
      text: processedText,
      direction,
      className,
      styles,
    };
  }

  /**
   * Generate appropriate CSS class name based on text analysis
   */
  private generateClassName(text: string, direction: 'rtl' | 'ltr' | 'auto'): string {
    const analysis = this.analyzeText(text);
    
    const classes = ['persian-text'];
    
    if (analysis.containsPersian) {
      classes.push('has-persian');
    }
    
    if (analysis.isPrimaryPersian) {
      classes.push('primary-persian');
    }
    
    classes.push(`text-${direction}`);
    
    return classes.join(' ');
  }

  /**
   * Validate Persian text input
   */
  validatePersianText(text: string): {
    isValid: boolean;
    errors: string[];
    suggestions: string[];
  } {
    const validation = validatePersianText(text);
    const suggestions: string[] = [];

    if (!validation.isValid) {
      // Add helpful suggestions based on errors
      validation.errors.forEach(error => {
        if (error.includes('empty')) {
          suggestions.push('لطفاً متن فارسی وارد کنید');
        } else if (error.includes('Invalid characters')) {
          suggestions.push('لطفاً فقط از حروف فارسی استفاده کنید');
        } else if (error.includes('Persian characters')) {
          suggestions.push('متن باید شامل حروف فارسی باشد');
        }
      });
    }

    return {
      ...validation,
      suggestions,
    };
  }

  /**
   * Convert between Persian and English digits
   */
  convertDigits(text: string, toPersian: boolean = true): string {
    return toPersian ? toPersianDigits(text) : toEnglishDigits(text);
  }

  /**
   * Get font loading status
   */
  getFontLoadingStatus(): {
    totalFonts: number;
    loadedFonts: number;
    loadedFontNames: string[];
    isLoading: boolean;
  } {
    return {
      totalFonts: this.fontLoadPromises.size,
      loadedFonts: this.loadedFonts.size,
      loadedFontNames: Array.from(this.loadedFonts),
      isLoading: this.fontLoadPromises.size > this.loadedFonts.size,
    };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<PersianLanguageConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Reapply CSS variables if they changed
    if (newConfig.cssVariables) {
      this.cssVariablesApplied = false;
      this.applyCSSVariables();
    }
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    // Remove CSS variables
    const styleElement = document.getElementById('persian-language-variables');
    if (styleElement) {
      styleElement.remove();
    }

    // Remove metadata styles
    const metadataStyles = document.querySelectorAll('.persian-metadata-styles');
    metadataStyles.forEach(style => style.remove());

    // Clear caches
    this.loadedFonts.clear();
    this.fontLoadPromises.clear();
    this.cssVariablesApplied = false;
  }

  /**
   * Create a Persian text component helper
   */
  createPersianTextElement(
    text: string, 
    tagName: string = 'span',
    options: PersianStyleOptions = {}
  ): HTMLElement {
    const element = document.createElement(tagName);
    element.textContent = text;
    
    this.applyPersianStyles(element, text, options);
    
    return element;
  }

  /**
   * Batch process multiple text elements
   */
  batchProcessElements(elements: HTMLElement[]): void {
    elements.forEach(element => {
      const text = element.textContent || '';
      if (containsPersian(text)) {
        this.applyPersianStyles(element, text);
      }
    });
  }

  /**
   * Get Persian language statistics for the current page
   */
  getPageStatistics(): {
    totalElements: number;
    persianElements: number;
    mixedElements: number;
    loadedFonts: string[];
    averageTextLength: number;
  } {
    const allElements = document.querySelectorAll('*');
    let totalElements = 0;
    let persianElements = 0;
    let mixedElements = 0;
    let totalTextLength = 0;

    allElements.forEach(element => {
      const text = element.textContent || '';
      if (text.trim().length > 0) {
        totalElements++;
        totalTextLength += text.length;
        
        const analysis = this.analyzeText(text);
        if (analysis.containsPersian) {
          if (analysis.isPrimaryPersian) {
            persianElements++;
          } else {
            mixedElements++;
          }
        }
      }
    });

    return {
      totalElements,
      persianElements,
      mixedElements,
      loadedFonts: Array.from(this.loadedFonts),
      averageTextLength: totalElements > 0 ? totalTextLength / totalElements : 0,
    };
  }
}

// Create and export singleton instance
export const persianLanguageService = new PersianLanguageService();

// Export the class for custom instances
export { PersianLanguageService };

// Export utility functions for direct use
export {
  containsPersian,
  isPrimaryPersian,
  validatePersianText,
  normalizePersianText,
  toPersianDigits,
  toEnglishDigits,
  getTextDirection,
} from '../utils/persianUtils';